// ProfileImage.js
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import * as Yup from 'yup';
import AdminUserDetail from '../../utilities/api/user/AdminUserDetail';
import { updateUser } from '../../redux/slices/userSlice';
import { ImageBaseURL } from '../../utilities/api/axios';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Chip from '@mui/material/Chip';
import FaceIcon from '@mui/icons-material/Face';
import DialogActions from '@mui/material/DialogActions';
import { useNavigate } from 'react-router-dom';

const ProfileImage = ({ user, refreshUserData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [editMode, setEditMode] = React.useState(false);
  const [profilePic, setProfilePic] = React.useState(null);
  const [frtStatus, setFrtStatus] = React.useState(null);
  const [frtDialogOpen, setFrtDialogOpen] = React.useState(false);

  const handleEdit = () => {
    setEditMode(true);
  };
  const handleFrtStatusClick = async () => {
    try {
      await fetchFrtStatus();
      setFrtDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch FRT status:', error);
    }
  };

  const handleCloseFrtDialog = () => {
    setFrtDialogOpen(false);
  };

  const handleSave = async values => {
    try {
      // Check if there's a profile picture to upload
      if (profilePic) {
        // Create a FormData object to handle file uploads along with other form data
        const formData = new FormData();
        formData.append('profile_pic', profilePic); // Append the profile picture
        formData.append('username', values.username); // Append other necessary form data fields

        // Update the user details on the server with form data including the profile picture
        await AdminUserDetail.AdminDetailUpdate(user.id, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        // Update the user details without profile picture
        await AdminUserDetail.AdminDetailUpdate(user.id, values);
      }

      // Fetch the latest user data to ensure state consistency
      const freshUserData = await AdminUserDetail.AdminDetailGet(user.id);

      // Update the Redux state with the latest fetched user data
      dispatch(updateUser(freshUserData));

      // Exit edit mode after successful save
      await refreshUserData();
      setEditMode(false);
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .required('Username is required')
      .matches(/^\S*$/, 'Username cannot contain spaces'), // Regex to disallow spaces
  });

  const formik = useFormik({
    initialValues: { username: user.username },
    enableReinitialize: true,
    validationSchema,
    onSubmit: values => handleSave(values),
  });

  const handleProfilePicChange = event => {
    setProfilePic(event.currentTarget.files[0]);
  };
  const fetchFrtStatus = async () => {
    try {
      const frtStatus = await AdminUserDetail.AdminDetailGetFRTStatus();
      setFrtStatus(frtStatus);
    } catch (error) {
      console.error('Error fetching FRT status:', error);
    }
  };
  const profilePicUrl = user.profile_pic
    ? `${ImageBaseURL}${user.profile_pic}`
    : '';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        margin: '1rem',
        width: '95%',
        border: '1px solid #D3D3D3',
        borderRadius: '10px',
        padding: '25px',
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
        <Avatar
          alt="Profile"
          src={profilePic ? URL.createObjectURL(profilePic) : profilePicUrl}
          sx={{ width: 200, height: 200, border: '1px solid #007BFF' }}
        />
        <Box sx={{ marginLeft: '3rem' }}>
          {editMode ? (
            <>
              <TextField
                label="Username"
                variant="outlined"
                name="username"
                value={formik.values.username}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.username && Boolean(formik.errors.username)
                }
                helperText={formik.touched.username && formik.errors.username}
                fullWidth
              />
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-pic-upload"
                type="file"
                onChange={handleProfilePicChange}
              />
              <label htmlFor="profile-pic-upload">
                <Button
                  variant="contained"
                  component="span"
                  sx={{ marginTop: '1rem' }}
                >
                  Upload Profile Picture
                </Button>
              </label>
            </>
          ) : (
            <>
              <Typography
                variant="h4"
                sx={{ color: '#007BFF', fontWeight: '500', fontSize: '2.5rem' }}
              >
                {user.username}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  opacity: '0.7',
                  fontSize: '1.1rem',
                  color: '#A6A6A6',
                }}
              >
                {user.staff ? 'Staff' : 'Admin'}
              </Typography>
              <Box sx={{ marginTop: '1rem' }}>
                <Chip
                  icon={<FaceIcon sx={{ fontSize: '1rem' }} />}
                  label="FRT Image Status"
                  variant="outlined"
                  onClick={handleFrtStatusClick}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    height: '32px',
                    padding: '6px 12px',
                    '& .MuiChip-label': {
                      fontSize: '1rem',
                      fontWeight: '500',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
      {editMode ? (
        <Box
          sx={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            cursor: 'pointer',
          }}
        >
          <Typography
            onClick={formik.handleSubmit}
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: '4px 10px',
              color: '#007BFF',
              fontSize: '1.1rem',
              border: '1px solid #007BFF',
              borderRadius: '20px',
              cursor: 'pointer',
            }}
          >
            Save
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            cursor: 'pointer',
          }}
          onClick={handleEdit}
        >
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: '4px 10px',
              color: '#007BFF',
              fontSize: '1.1rem',
              border: '1px solid #007BFF',
              borderRadius: '20px',
            }}
          >
            Edit
            <EditIcon
              sx={{ fontSize: '1rem', color: '#007BFF', marginLeft: '5px' }}
            />
          </Typography>
        </Box>
      )}
      <Dialog
        open={frtDialogOpen}
        onClose={handleCloseFrtDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: 'rgb(39,63,119)',
            color: 'white',
            textAlign: 'center',
          }}
        >
          FRT Image Status
        </DialogTitle>
        <DialogContent sx={{ padding: '1rem' }}>
          {frtStatus ? (
            <Box>
              {frtStatus.error ? (
                <Typography
                  variant="h6"
                  color="error"
                  sx={{ textAlign: 'center' }}
                >
                  {frtStatus.error}
                </Typography>
              ) : (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      marginBottom: '0.5rem',
                      color: 'rgb(39,63,119)',
                      fontSize: '1.1rem',
                    }}
                  >
                    Status Information:
                  </Typography>

                  {/* Status Display with Icon */}
                  <Box
                    sx={{
                      backgroundColor: '#f5f5f5',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                      textAlign: 'center',
                      marginBottom: '0.8rem',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                      }}
                    >
                      {/* Status Icon with Tooltip */}
                      <Box
                        sx={{ position: 'relative', display: 'inline-block' }}
                      >
                        <span
                          style={{
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                          }}
                          title={
                            frtStatus.status === 'Failed'
                              ? 'Failed'
                              : frtStatus.status === 'Success'
                                ? 'Success'
                                : frtStatus.status === 'Pending'
                                  ? 'Pending'
                                  : frtStatus.status
                          }
                        >
                          {frtStatus.status === 'Failed'
                            ? '❌'
                            : frtStatus.status === 'Success'
                              ? '✅'
                              : frtStatus.status === 'Pending'
                                ? '⏳'
                                : '❓'}
                        </span>
                      </Box>

                      {/* Status Text */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          color:
                            frtStatus.status === 'Failed'
                              ? '#d32f2f'
                              : frtStatus.status === 'Success'
                                ? '#2e7d32'
                                : frtStatus.status === 'Pending'
                                  ? '#ed6c02'
                                  : 'rgb(39,63,119)',
                        }}
                      >
                        {frtStatus.status}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Failed Reason - Only show if status is Failed */}
                  {frtStatus.status === 'Failed' && frtStatus.failed_reason && (
                    <Box
                      sx={{
                        backgroundColor: '#ffebee',
                        padding: '0.8rem',
                        borderRadius: '6px',
                        border: '1px solid #ffcdd2',
                        marginBottom: '0.8rem',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 'bold',
                          color: '#d32f2f',
                          marginBottom: '0.3rem',
                          fontSize: '0.9rem',
                        }}
                      >
                        Reason for Failure:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#d32f2f', fontSize: '0.85rem' }}
                      >
                        {frtStatus.failed_reason}
                      </Typography>
                    </Box>
                  )}

                  {/* Re-upload Button - Only show if image_required is true */}
                  {frtStatus.image_required && (
                    <Box sx={{ textAlign: 'center', marginTop: '0.8rem' }}>
                      <Button
                        variant="contained"
                        startIcon={<span>📷</span>}
                        sx={{
                          backgroundColor: 'rgb(39,63,119)',
                          color: 'white',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          textTransform: 'none',
                          boxShadow: '0 2px 4px rgba(39,63,119,0.3)',
                          '&:hover': {
                            backgroundColor: 'rgb(29,53,109)',
                            boxShadow: '0 3px 6px rgba(39,63,119,0.4)',
                          },
                        }}
                        onClick={() => {
                          // Handle re-upload functionality here
                          navigate('/re-upload-instructions');
                          console.log('Re-upload image clicked');
                          // You can add your re-upload logic here
                        }}
                      >
                        RE UPLOAD Image
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              No FRT status data available
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '1rem', justifyContent: 'center' }}>
          <Typography
            onClick={handleCloseFrtDialog}
            sx={{
              color: 'rgb(39,63,119)',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Close
          </Typography>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileImage;

// import React from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { useFormik } from 'formik';
// import Avatar from '@mui/material/Avatar';
// import Box from '@mui/material/Box';
// import Typography from '@mui/material/Typography';
// import TextField from '@mui/material/TextField';
// import Button from '@mui/material/Button';
// import EditIcon from '@mui/icons-material/Edit'; // Import Edit icon from Material-UI
// import * as Yup from 'yup';
// import AdminUserDetail from '../../utilities/api/user/AdminUserDetail';
// import { updateUser } from '../../redux/slices/userSlice';
// import { ImageBaseURL } from "../../utilities/api/axios";

// const ProfileImage = () => {
//   const {user} = useSelector((state) => state.user.user); // Ensure it fetches the latest user data from the Redux store
//   const dispatch = useDispatch();
//   const [editMode, setEditMode] = React.useState(false);
//   const [profilePic, setProfilePic] = React.useState(null);

//   const handleEdit = () => {
//     setEditMode(true);
//   };

//   const handleSave = async (values) => {
//     try {
//       let updatedUser;

//       if (profilePic) {
//         const formData = new FormData();
//         formData.append('profile_pic', profilePic);
//         formData.append('username', values.username);

//         updatedUser = await AdminUserDetail.AdminDetailUpdate(user.id, formData, {
//           headers: {
//             'Content-Type': 'multipart/form-data',
//           },
//         });
//       } else {
//         updatedUser = await AdminUserDetail.AdminDetailUpdate(user.id, values);
//       }

//       console.log('User updated successfully:', updatedUser);
//       dispatch(updateUser(updatedUser)); // Dispatch the updateUser action with the updated user data
//       setEditMode(false);  // Exit edit mode
//     } catch (error) {
//       console.error('Error updating user:', error);
//     }
//   };

//   const validationSchema = Yup.object().shape({
//     username: Yup.string().required('Username is required'),
//   });

//   const formik = useFormik({
//     initialValues: {
//       username: user.username,
//     },
//     enableReinitialize: true, // This ensures Formik will update its values when the user changes
//     validationSchema: validationSchema,
//     onSubmit: (values) => {
//       console.log('Form submitted with values:', values);
//       handleSave(values);
//     },
//   });

//   const handleProfilePicChange = (event) => {
//     setProfilePic(event.currentTarget.files[0]);
//   };

//   const profilePicUrl = user.profile_pic ? `${ImageBaseURL}/${user.profile_pic}` : '';

//   return (
//     <Box
//       sx={{
//         display: 'flex',
//         alignItems: 'center',
//         margin: '1rem',
//         width: '95%',
//         border: '1px solid #D3D3D3',
//         borderRadius: '10px',
//         padding: '25px',
//         position: 'relative',
//       }}
//     >
//       <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
//         <Avatar
//           alt="Profile"
//           src={profilePic ? URL.createObjectURL(profilePic) : profilePicUrl}
//           sx={{ width: 200, height: 200, border: '1px solid #007BFF' }}
//         />
//         <Box sx={{ marginLeft: '3rem' }}>
//           {editMode ? (
//             <>
//               <TextField
//                 label="Username"
//                 variant="outlined"
//                 name="username"
//                 value={formik.values.username}
//                 onChange={formik.handleChange}
//                 onBlur={formik.handleBlur}
//                 error={formik.touched.username && Boolean(formik.errors.username)}
//                 helperText={formik.touched.username && formik.errors.username}
//                 fullWidth
//               />
//               <input
//                 accept="image/*"
//                 style={{ display: 'none' }}
//                 id="profile-pic-upload"
//                 type="file"
//                 onChange={handleProfilePicChange}
//               />
//               <label htmlFor="profile-pic-upload">
//                 <Button variant="contained" component="span" sx={{ marginTop: '1rem' }}>
//                   Upload Profile Picture
//                 </Button>
//               </label>
//             </>
//           ) : (
//             <>
//               <Typography
//                 variant="h4"
//                 sx={{ color: '#007BFF', fontWeight: '500', fontSize: '2.5rem' }}
//               >
//                 {user.username} {/* This will automatically update when the username is updated */}
//               </Typography>
//               <Typography variant="body2" sx={{ fontWeight: 700, opacity: '0.7', fontSize: '1.1rem', color: '#A6A6A6' }}>
//                 {user.staff ? 'Staff' : 'Admin'}
//               </Typography>
//             </>
//           )}
//         </Box>
//       </Box>
//       {editMode ? (
//         <Box
//           sx={{
//             position: 'absolute',
//             top: '12px',
//             right: '12px',
//             cursor: 'pointer',
//           }}
//         >
//           <Typography
//             onClick={formik.handleSubmit}
//             sx={{
//               display: 'flex',
//               alignItems: 'center',
//               padding: '4px 10px',
//               color: '#007BFF',
//               fontSize: '1.1rem',
//               border: '1px solid #007BFF',
//               borderRadius: '20px',
//               cursor: 'pointer',
//             }}
//           >
//             Save
//           </Typography>
//         </Box>
//       ) : (
//         <Box
//           sx={{
//             position: 'absolute',
//             top: '12px',
//             right: '12px',
//             cursor: 'pointer',
//           }}
//           onClick={handleEdit}
//         >
//           <Typography
//             sx={{
//               display: 'flex',
//               alignItems: 'center',
//               padding: '4px 10px',
//               color: '#007BFF',
//               fontSize: '1.1rem',
//               border: '1px solid #007BFF',
//               borderRadius: '20px',
//             }}
//           >
//             Edit
//             <EditIcon sx={{ fontSize: '1rem', color: '#007BFF', marginLeft: '5px' }} />
//           </Typography>
//         </Box>
//       )}
//     </Box>
//   );
// };

// export default ProfileImage;
